import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, ReplaySubject, Subject, tap } from 'rxjs';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';

@Injectable({
  providedIn: 'root',
})
export class QueryParamsService {
  public _currentParams: Params = {};
  _tabChanged$ = new Subject<eTab>();
  private _initialParamsProcessed = false;
  private _isNavigatingToSharedUrl = false;

  queryParams$: ReplaySubject<Params> = new ReplaySubject<Params>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router
  ) {
    this.initializeQueryParamsSubscription();
  }

  get currentParams(): Params {
    return this._currentParams;
  }

  get initialParamsProcessed(): boolean {
    return this._initialParamsProcessed;
  }

  get isNavigatingToSharedUrl(): boolean {
    return this._isNavigatingToSharedUrl;
  }

  markInitialParamsAsProcessed(): void {
    this._initialParamsProcessed = true;
  }

  private initializeQueryParamsSubscription(): void {
    // Check if we're loading with URL parameters
    const snapshot = this.activatedRoute.snapshot.queryParams;
    if (snapshot && (snapshot['tab'] || snapshot['routeId'])) {
      // Set flag to prevent default tab selection during initialization
      this._isNavigatingToSharedUrl = true;
      console.debug('Detected shared URL with parameters:', snapshot);

      // If routeId is present but tab is not set to source-destination or lines, set it
      if (
        snapshot['routeId'] &&
        (!snapshot['tab'] || (snapshot['tab'] !== eTab.SOURCE_DESTINATION && snapshot['tab'] !== eTab.LINES))
      ) {
        console.debug(
          'Setting tab to SOURCE_DESTINATION for routeId parameter'
        );
        this.setQueryParam('tab', eTab.SOURCE_DESTINATION);
      }
    }

    this.activatedRoute.queryParams
      .pipe(
        tap((params) => {
          console.debug('Query params received:', params);
          this._currentParams = params;
        }),
        tap((params) => this.queryParams$.next(params)),
        tap((params) => {
          if (
            !!params['tab'] &&
            typeof params['tab'] === 'string' &&
            !this._initialParamsProcessed
          ) {
            console.debug('Emitting tab change for:', params['tab']);
            this._tabChanged$.next(eTab[params['tab']]);

            // If this is a shared URL, mark as processed after emitting the tab change
            if (this._isNavigatingToSharedUrl) {
              setTimeout(() => {
                this._initialParamsProcessed = true;
                this._isNavigatingToSharedUrl = false;
              }, 100);
            }
          }
        })
      )
      .subscribe();
  }

  clearQueryParams(excludedParams?: string[]) {
    // Don't clear params during initial shared URL navigation
    if (this._isNavigatingToSharedUrl) {
      console.debug('Skipping clearQueryParams during shared URL navigation');
      return;
    }

    // Always preserve the routeId parameter unless explicitly asked to clear it
    // const defaultExcludedParams = ['routeId'];
    const defaultExcludedParams = [];
    const mergedExcludedParams = excludedParams
      ? [...new Set([...defaultExcludedParams, ...excludedParams])]
      : defaultExcludedParams;

    const paramsToClear = Object.keys(this._currentParams)
      .filter((queryParam) => !mergedExcludedParams.includes(queryParam))
      .reduce(
        (params: Params, queryParam) => ({
          ...params,
          [queryParam]: null,
        }),
        {}
      );

    this.setQueryParams(paramsToClear);
  }

  setQueryParam(queryParam: string, value: any) {
    this._currentParams = {
      ...this._currentParams,
      [queryParam]: value,
    };

    const url = this.router
      .createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: this._currentParams,
        queryParamsHandling: 'merge',
      })
      .toString();

    this.router.navigateByUrl(url);
  }

  setQueryParams(queryParams: Params) {
    const params = Object.keys(queryParams);

    params.forEach((param) => {
      this._currentParams = {
        ...this._currentParams,
        [param]: queryParams[param],
      };
    });

    const url = this.router
      .createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: this._currentParams,
        queryParamsHandling: 'merge',
      })
      .toString();
    this.router.navigateByUrl(url);
  }
}
